import {
  applySnapshot,
  flow,
  getParent,
  Instance,
  types,
} from "mobx-state-tree";
import type { IAnyStateTreeNode } from "mobx-state-tree";
import { GetSurveyRulesRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRulesRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetSurveyRulesRPCClientImpl } from "../rpcs/RPC";
import type { SurveyRule } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRule";

interface GetSurveyRulesStoreInitialState {
  loadingGetSurveyRules: boolean;
  getSurveyRulesRPCError: GetSurveyRulesRPC.RPCError | null;
}

interface SurveyRuleStoreParent extends IAnyStateTreeNode {
  setRules: (rules: SurveyRule[]) => void;
}

const getInitialState = (): GetSurveyRulesStoreInitialState => ({
  loadingGetSurveyRules: false,
  getSurveyRulesRPCError: null,
});

export const GetSurveyRulesStore = types
  .model("GetSurveyRulesStore", {
    loadingGetSurveyRules: types.boolean,
    getSurveyRulesRPCError: types.maybeNull(
      types.enumeration(
        "GetSurveyRulesRPCError",
        Object.values(GetSurveyRulesRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getSurveyRules: flow(function* (surveyId: string) {
      try {
        store.getSurveyRulesRPCError = null;
        store.loadingGetSurveyRules = true;

        const apiClient = getAPIClient(store);
        const request = new GetSurveyRulesRPC.Request(new LeoUUID(surveyId));

        const result: LeoRPCResult<
          GetSurveyRulesRPC.Response,
          GetSurveyRulesRPC.Errors.Errors
        > = yield useGetSurveyRulesRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          const surveyRuleStore = getParent<SurveyRuleStoreParent>(store);
          surveyRuleStore.setRules(result.response.surveyRules);
          return;
        }

        switch (result.error.code) {
          case GetSurveyRulesRPC.RPCError.InvalidSurveyId:
            store.getSurveyRulesRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingGetSurveyRules = false;
      }
    }),

    resetStore: (): void => {
      applySnapshot(store, getInitialState());
    },
  }));

export const createGetSurveyRulesStore = (): Instance<
  typeof GetSurveyRulesStore
> => GetSurveyRulesStore.create(getInitialState());
